import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  signupEmailApi,
  loginEmailApi,
  loginGoogleApi,
  checkSlugApi,
  createOrganizationApi,
  listMyOrganizationsApi,
  getOrganizationLicenseApi,
  changeOrganizationLicenseApi,
} from '../../api/controlPlaneApi'
import {
  ControlPlaneSession,
  CreateOrganizationPayload,
  CreateOrganizationResult,
  OnboardedOrganization,
} from '../../types/controlPlane'
import { License, LicensePlan } from '../../types/license'

const rejectMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback

export const signupEmail = createAsyncThunk<
  ControlPlaneSession,
  { email: string; password: string; firstName: string; lastName: string },
  { rejectValue: string }
>('controlPlane/signupEmail', async (payload, thunkAPI) => {
  try {
    return await signupEmailApi(payload)
  } catch (error: any) {
    return thunkAPI.rejectWithValue(rejectMessage(error, 'Signup failed.'))
  }
})

export const loginEmail = createAsyncThunk<
  ControlPlaneSession,
  { email: string; password: string },
  { rejectValue: string }
>('controlPlane/loginEmail', async (payload, thunkAPI) => {
  try {
    return await loginEmailApi(payload)
  } catch (error: any) {
    return thunkAPI.rejectWithValue(rejectMessage(error, 'Login failed.'))
  }
})

export const loginGoogle = createAsyncThunk<ControlPlaneSession, string, { rejectValue: string }>(
  'controlPlane/loginGoogle',
  async (idToken, thunkAPI) => {
    try {
      return await loginGoogleApi(idToken)
    } catch (error: any) {
      return thunkAPI.rejectWithValue(rejectMessage(error, 'Google sign-in failed.'))
    }
  }
)

export const checkSlug = createAsyncThunk<
  { slug: string; available: boolean },
  string,
  { rejectValue: string }
>('controlPlane/checkSlug', async (slug, thunkAPI) => {
  try {
    return await checkSlugApi(slug)
  } catch (error: any) {
    return thunkAPI.rejectWithValue(rejectMessage(error, 'Could not check availability.'))
  }
})

export const createOrganization = createAsyncThunk<
  CreateOrganizationResult,
  CreateOrganizationPayload,
  { rejectValue: string }
>('controlPlane/createOrganization', async (payload, thunkAPI) => {
  try {
    return await createOrganizationApi(payload)
  } catch (error: any) {
    return thunkAPI.rejectWithValue(rejectMessage(error, 'Could not create the organization.'))
  }
})

export const fetchMyOrganizations = createAsyncThunk<
  OnboardedOrganization[],
  void,
  { rejectValue: string }
>('controlPlane/fetchMyOrganizations', async (_, thunkAPI) => {
  try {
    return await listMyOrganizationsApi()
  } catch (error: any) {
    return thunkAPI.rejectWithValue(rejectMessage(error, 'Could not load your organizations.'))
  }
})

export const fetchOrganizationLicense = createAsyncThunk<
  { organizationId: string; license: License },
  string,
  { rejectValue: string }
>('controlPlane/fetchOrganizationLicense', async (organizationId, thunkAPI) => {
  try {
    const license = await getOrganizationLicenseApi(organizationId)
    return { organizationId, license }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(rejectMessage(error, 'Could not load the license.'))
  }
})

export const changeOrganizationLicense = createAsyncThunk<
  { organizationId: string; license: License },
  { organizationId: string; planKey: LicensePlan },
  { rejectValue: string }
>('controlPlane/changeOrganizationLicense', async ({ organizationId, planKey }, thunkAPI) => {
  try {
    const license = await changeOrganizationLicenseApi(organizationId, planKey)
    return { organizationId, license }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(rejectMessage(error, 'Could not change the plan.'))
  }
})
